import type { AccountMeta, TransactionInstruction } from '@solana/web3.js';
import { PublicKey } from '@solana/web3.js';

export const SPONSORSHIP_INSTRUCTION_NAMES = [
  'back',
  'buy',
  'sell',
  'boostMarket',
  'claimPrize',
  'claimBackerFees',
  'createMarketShell',
  'initOutcome',
  'swapBaseInput',
] as const;

export type SponsorshipInstructionName = (typeof SPONSORSHIP_INSTRUCTION_NAMES)[number];

type SponsorshipProgram = 'markets' | 'cpSwap';

interface SponsorshipInstructionSpec {
  readonly idlInstructionName: string;
  readonly program: SponsorshipProgram;
}

const SPECS: Readonly<Record<SponsorshipInstructionName, SponsorshipInstructionSpec>> = {
  back: { idlInstructionName: 'back', program: 'markets' },
  buy: { idlInstructionName: 'buy', program: 'markets' },
  sell: { idlInstructionName: 'sell', program: 'markets' },
  boostMarket: { idlInstructionName: 'boost_market', program: 'markets' },
  claimPrize: { idlInstructionName: 'claim_prize', program: 'markets' },
  claimBackerFees: { idlInstructionName: 'claim_backer_fees', program: 'markets' },
  createMarketShell: { idlInstructionName: 'create_market_shell', program: 'markets' },
  initOutcome: { idlInstructionName: 'init_outcome', program: 'markets' },
  swapBaseInput: { idlInstructionName: 'swap_base_input', program: 'cpSwap' },
};

export interface SponsorshipInstructionLayout {
  readonly name: SponsorshipInstructionName;
  readonly program: SponsorshipProgram;
  readonly programId: PublicKey;
  readonly discriminator: readonly number[];
  readonly accountNames: readonly string[];
  readonly accountCount: number;
  accountIndex(name: string): number | null;
}

export interface DecodedSponsorshipInstruction {
  readonly layout: SponsorshipInstructionLayout;
  readonly instruction: TransactionInstruction;
  readonly remainingAccounts: readonly AccountMeta[];
  account(name: string): AccountMeta | null;
}

type SponsorshipLayouts = ReadonlyMap<SponsorshipInstructionName, SponsorshipInstructionLayout>;

interface SponsorshipIdl {
  readonly address: string;
  readonly instructions: readonly SponsorshipIdlInstruction[];
}

interface SponsorshipIdlInstruction {
  readonly name: string;
  readonly discriminator: readonly number[];
  readonly accounts: readonly SponsorshipIdlAccount[];
}

interface SponsorshipIdlAccount {
  readonly name: string;
}

let layoutsPromise: Promise<SponsorshipLayouts> | null = null;

/**
 * Loads protocol instruction layouts directly from the committed Markets and
 * CP-swap IDLs. Sponsorship policies address accounts by name, never by a
 * copied discriminator or numeric position.
 */
export async function getSponsorshipInstructionLayout(
  name: SponsorshipInstructionName,
): Promise<SponsorshipInstructionLayout> {
  const layouts = await loadSponsorshipLayouts();
  const layout = layouts.get(name);
  if (layout === undefined) {
    throw new Error(`Missing sponsorship layout for ${name}`);
  }
  return layout;
}

/**
 * Decodes an instruction only when its program ID and discriminator match a
 * supported protocol layout. Extra accounts are preserved as remaining accounts.
 */
export async function decodeSponsorshipInstruction(
  instruction: TransactionInstruction,
): Promise<DecodedSponsorshipInstruction | null> {
  const layouts = await loadSponsorshipLayouts();
  for (const layout of layouts.values()) {
    if (!instruction.programId.equals(layout.programId)) continue;
    if (!hasDiscriminator(instruction.data, layout.discriminator)) continue;
    if (instruction.keys.length < layout.accountCount) return null;
    return decodedInstruction(layout, instruction);
  }
  return null;
}

async function loadSponsorshipLayouts(): Promise<SponsorshipLayouts> {
  if (layoutsPromise === null) {
    layoutsPromise = createSponsorshipLayouts();
  }
  return layoutsPromise;
}

async function createSponsorshipLayouts(): Promise<SponsorshipLayouts> {
  const [marketsModule, cpSwapModule] = await Promise.all([
    import('../../../../../contracts/markets/idl/markets.json', { with: { type: 'json' } }),
    import('../../../../../contracts/morfi-cp-swap/idl/raydium_cp_swap.json', {
      with: { type: 'json' },
    }),
  ]);
  const idls: Readonly<Record<SponsorshipProgram, SponsorshipIdl>> = {
    markets: marketsModule.default,
    cpSwap: cpSwapModule.default,
  };
  const layouts = new Map<SponsorshipInstructionName, SponsorshipInstructionLayout>();
  for (const name of SPONSORSHIP_INSTRUCTION_NAMES) {
    const spec = SPECS[name];
    const idl = idls[spec.program];
    const instruction = idl.instructions.find(
      (candidate) => candidate.name === spec.idlInstructionName,
    );
    if (instruction === undefined) {
      throw new Error(`IDL missing sponsorship instruction ${spec.idlInstructionName}`);
    }
    layouts.set(
      name,
      layoutFromIdl(
        name,
        spec.program,
        idl.address,
        instruction.discriminator,
        instruction.accounts,
      ),
    );
  }
  return layouts;
}

function layoutFromIdl(
  name: SponsorshipInstructionName,
  program: SponsorshipProgram,
  address: string,
  discriminator: readonly number[],
  accounts: readonly SponsorshipIdlAccount[],
): SponsorshipInstructionLayout {
  const accountNames = accounts.map((account) => account.name);
  const indexes = new Map(accountNames.map((accountName, index) => [accountName, index]));
  return {
    name,
    program,
    programId: new PublicKey(address),
    discriminator: Object.freeze([...discriminator]),
    accountNames: Object.freeze(accountNames),
    accountCount: accountNames.length,
    accountIndex(accountName: string): number | null {
      return indexes.get(accountName) ?? null;
    },
  };
}

function decodedInstruction(
  layout: SponsorshipInstructionLayout,
  instruction: TransactionInstruction,
): DecodedSponsorshipInstruction {
  return {
    layout,
    instruction,
    remainingAccounts: instruction.keys.slice(layout.accountCount),
    account(name: string): AccountMeta | null {
      const index = layout.accountIndex(name);
      return index === null ? null : (instruction.keys[index] ?? null);
    },
  };
}

function hasDiscriminator(data: Uint8Array, discriminator: readonly number[]): boolean {
  if (data.length < discriminator.length) return false;
  return discriminator.every((byte, index) => data[index] === byte);
}
